import { PrimaryCTA } from "@core/components/cta/PrimaryCTA";
import { promptEditorExtensions } from "@core/domains/chat/prompt/extensions/getExtensions";
import { getPromptTextAndDecorations } from "@core/domains/chat/prompt/getPromptTextAndDecorations";
import { transformPastedHTML } from "@core/domains/chat/prompt/pasteTransform";
import { transformPastedText } from "@core/domains/chat/prompt/pasteTransform";
import { CollectionKind } from "@core/domains/collections/collections.types";
import { useWire } from "@core/wire";
import { APIError, type curator, getRequestClient } from "@repo/client";
import type { JSONContent } from "@tiptap/core";
import Dialog from "corvu/dialog";
import { TbDeviceFloppy } from "solid-icons/tb";
import { createEffect, createSignal, type JSXElement, type Component, createMemo, on } from "solid-js";
import { createEditor, EditorContent } from "tiptap-solid";
import { SavePromptCollectionPickerModal } from "./SavePromptCollectionPickerModal";
import { useUIState } from "@core/ui/UIState";

export const SaveToPromptLibraryModal: Component<{
  open: boolean;
  setOpen: (open: boolean) => void;
  initialPrompt: string | JSONContent;
  existingPrompt?: curator.StoredPromptSnapshot;
  trigger?: JSXElement;
  onSave?: () => void;
}> = (props) => {
  return (
    <Dialog open={props.open} onOpenChange={props.setOpen} trapFocus={false}>
      {props.trigger}

      <Dialog.Portal>
        <Dialog.Overlay class="fixed inset-0 z-max bg-black/50 data-[open]:animate-fade-in data-[closed]:animate-fade-out transition-opacity" />
        <Content
          existingPrompt={props.existingPrompt}
          initialPrompt={props.initialPrompt}
          setOpen={props.setOpen}
          open={props.open}
          onSave={props.onSave}
        />
      </Dialog.Portal>
    </Dialog>
  );
};

const Content: Component<{
  existingPrompt?: curator.StoredPromptSnapshot;
  initialPrompt: string | JSONContent;
  setOpen: (open: boolean) => void;
  open: boolean;
  onSave?: () => void;
}> = (props) => {
  const wire = useWire();
  const ui = useUIState();
  const client = getRequestClient(wire.services.auth.token);
  const [, setDrawer] = ui.promptLibraryDrawer;
  const [collectionPicker, setCollectionPicker] = createSignal(false);
  const [activeCollection, setActiveCollection] = createSignal<string>(
    props.existingPrompt?.collectionID ||
      wire.services.collections.getRootCollectionByKind(CollectionKind.Personal)?.id ||
      "",
  );

  const editor = createEditor({
    extensions: promptEditorExtensions(),
    content: props.initialPrompt,
    editorProps: {
      attributes: {
        class: "outline-none min-h-[1.8125rem] max-h-[224px] sm:max-h-[400px] overflow-y-auto break-words text-[16px]",
        "data-testid": "prompt-text-input",
        "aria-label": "Type your prompt",
      },
      transformPastedText(text) {
        return transformPastedText(text);
      },
      transformPastedHTML(html) {
        return transformPastedHTML(html);
      },
    },
  });

  let once = false;
  createEffect(() => {
    if (once) return;
    if (editor()) {
      once = true;
      editor()?.commands.setContent(props.initialPrompt, true, { preserveWhitespace: "full" });
    }
  });

  const [error, setError] = createSignal<string | null>(null);

  const [loading, setLoading] = createSignal(false);
  const onSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (loading()) return;

    setLoading(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const active = wire.services.collections.getCollection(activeCollection());

    if (editor()?.isEmpty) {
      setError("Prompt cannot be empty");
      setLoading(false);
      return;
    }

    if (!active) {
      setLoading(false);
      return;
    }
    const name = formData.get("name") as string;
    const description = formData.get("description") as string;
    const prompt = getPromptTextAndDecorations(editor());

    try {
      if (props.existingPrompt) {
        await client.controlplane.UpdateStoredPrompt(props.existingPrompt.id, {
          id: props.existingPrompt.id,
          name,
          description,
          prompt: prompt.text || "",
          collectionId: active.id,
        });
      } else {
        await client.controlplane.CreateStoredPrompt(active.id, {
          name,
          description,
          prompt: prompt.text || "",
          collectionId: active.id,
          organizationContext: {
            organizationId: active.organizationId,
            tenantId: active.tenantId,
          },
        });
      }
      props.setOpen(false);
      setDrawer(true);
      props.onSave?.();
    } catch (error) {
      console.error(error);
      if (error instanceof APIError) {
        setError(error.message);
      } else {
        setError("An unknown error occurred");
      }
    }
    setLoading(false);
  };

  return (
    <Dialog.Content
      class={`text-on-background fixed left-1/2 top-1/2 z-max -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background px-6 py-5 w-full max-w-[50rem]
    data-[open]:animate-dialog-open data-[closed]:animate-dialog-close transition-opacity origin-top`}
    >
      <Dialog.Label class="type-title-sm mb-6">
        {props.existingPrompt ? "Edit prompt" : "Save to Prompt Library"}
      </Dialog.Label>
      <form onSubmit={onSubmit} class="text-on-background">
        <div class="flex flex-col gap-4 mb-4">
          <label class="flex flex-col gap-2">
            <span>Name</span>
            <input
              required
              value={props.existingPrompt?.name || ""}
              type="text"
              name="name"
              class="bg-surface border border-solid border-surface-decoration rounded px-2 py-1"
            />
          </label>

          <label class="flex flex-col gap-2">
            <span>Description</span>
            <input
              required
              value={props.existingPrompt?.description || ""}
              type="text"
              name="description"
              class="bg-surface border border-solid border-surface-decoration rounded px-2 py-1"
            />
          </label>

          <div class="flex flex-col gap-2">
            <span>Prompt</span>

            <EditorContent
              data-prompt-container
              class={
                "text-base font-light flex-auto cursor-text dark:text-white bg-surface border border-solid border-surface-decoration rounded px-2 py-1"
              }
              onClick={(e) => {
                if (e.currentTarget === e.target) {
                  editor()?.commands.focus("all");
                }
              }}
              editor={editor()}
            />
          </div>

          <SavePromptCollectionPickerModal
            open={collectionPicker()}
            setOpen={setCollectionPicker}
            triggerClass="w-full"
            collectionId={activeCollection()}
            onCollectionSelected={(id) => {
              setActiveCollection(id);
              setCollectionPicker(false);
            }}
          />
        </div>
        <p class="text-danger">{error()}</p>

        <div class="flex justify-end">
          <PrimaryCTA
            type="submit"
            loading={loading()}
            icon={TbDeviceFloppy}
            data-test-id="save-to-prompt-library-cta"
            label="Save"
            accessibleSuffix=" to prompt library"
          />
        </div>
      </form>
    </Dialog.Content>
  );
};
